import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatGameCreated, getPlaytestResultMeta } from "./developerUtils";
import { getPlaytestTypeLabel } from "../../utils/playtestTypes";

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function GameList({
  games,
  variant = "table",
  limit,
  showToolbar = true,
  showCreateButton = false,
  onCreateClick,
  basePath = "/developer",
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredGames = useMemo(() => {
    return games.filter((game) => {
      const matchesSearch = game.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || game.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [games, searchQuery, statusFilter]);

  // Developer-local sequence (1 = first game they created), not the global DB id.
  const developerGameNumberById = useMemo(() => {
    const sorted = [...games].sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      if (aTime !== bTime) return aTime - bTime;
      return Number(a._id || a.id || 0) - Number(b._id || b.id || 0);
    });
    const map = new Map();
    sorted.forEach((game, index) => {
      map.set(String(game._id || game.id), index + 1);
    });
    return map;
  }, [games]);

  const displayGames = limit ? filteredGames.slice(0, limit) : filteredGames;

  const openReports = (gameId) => {
    navigate(`${basePath}/reports?game=${gameId}`);
  };

  if (variant !== "table") {
    return null;
  }

  return (
    <>
      {showToolbar && (
        <div className="dev-card-header">
          <h2 className="dev-card-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
            Game History
          </h2>
          <div className="dev-table-toolbar">
            <select
              className="dev-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              type="text"
              className="dev-search"
              placeholder="Search games..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {showCreateButton && (
              <button type="button" className="dev-btn dev-btn--primary dev-btn--sm" onClick={onCreateClick}>
                Create Game
              </button>
            )}
          </div>
        </div>
      )}

      {displayGames.length === 0 ? (
        <div className="dev-empty">
          {games.length === 0
            ? "You haven't created any games yet. Create your first game to get started."
            : "No games match your search criteria."}
          {showCreateButton && games.length === 0 && (
            <div style={{ marginTop: "1rem" }}>
              <button type="button" className="dev-btn dev-btn--primary" onClick={onCreateClick}>
                Create Your First Game
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="dev-table-wrap">
          <table className="dev-table">
            <thead>
              <tr>
                <th>Game</th>
                <th>Description</th>
                <th>Playtest results</th>
                <th>Created</th>
                <th>Status</th>
                {!limit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {displayGames.map((game) => {
                const gameId = game._id || game.id;
                const developerGameNumber = developerGameNumberById.get(String(gameId)) || "—";
                const result = getPlaytestResultMeta(game);
                const status = (game.status || "pending").toLowerCase();
                const playtestLabel = getPlaytestTypeLabel(game);

                return (
                  <tr key={gameId}>
                    <td>
                      <span className="dev-table-game-id">Game #{developerGameNumber}</span>
                      <div className="dev-table-game-name">{game.name || "Untitled"}</div>
                      {playtestLabel && (
                        <div className="dev-table-playtest-type">{playtestLabel}</div>
                      )}
                    </td>
                    <td>
                      <span className="dev-table-desc" title={game.description || ""}>
                        {game.description || "—"}
                      </span>
                    </td>
                    <td>
                      <span className={`dev-result dev-result--${result.variant}`}>
                        {result.variant === "pass" && <CheckIcon />}
                        {result.variant === "warn" && <WarnIcon />}
                        {result.label}
                      </span>
                    </td>
                    <td>{formatGameCreated(game.createdAt)}</td>
                    <td>
                      <span className={`dev-status dev-status--${status}`}>
                        {game.status || "pending"}
                      </span>
                    </td>
                    {!limit && (
                      <td>
                        <button
                          type="button"
                          className="dev-btn dev-btn--ghost dev-btn--sm"
                          onClick={() => openReports(gameId)}
                        >
                          View Reports
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default GameList;
